"use client"

import { useMemo } from "react"
import { motion } from "framer-motion"
import { Clock, FileText } from "lucide-react"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Separator } from "@/components/ui/separator"
import type { DailyEntry } from "@/lib/db/schema"
import { getZoneById } from "@/lib/zones"
import { formatCzechDate, toLocalDateString } from "@/lib/date-utils"
import { cn } from "@/lib/utils"

interface DayDetailSheetProps {
  date: Date | null
  entries: DailyEntry[]
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Format time from HH:MM:SS to HH:MM
 */
function formatTime(timestamp: string): string {
  const [hours, minutes] = timestamp.split(":")
  return `${hours}:${minutes}`
}

/**
 * DayDetailSheet - Modal showing all entries for a selected day
 *
 * Features:
 * - Timeline view with chronological sorting
 * - Zone emoji, name, and color for each entry
 * - Time display for each entry
 * - Notes display (if present)
 * - Empty state for days without entries
 * - Responsive mobile/desktop design
 */
export function DayDetailSheet({
  date,
  entries,
  open,
  onOpenChange,
}: DayDetailSheetProps) {
  // Filter and sort entries for the selected date
  const dayEntries = useMemo(() => {
    if (!date) return []

    const dateStr = toLocalDateString(date)
    return entries
      .filter((entry) => entry.date === dateStr)
      .sort((a, b) => a.timestamp.localeCompare(b.timestamp))
  }, [date, entries])

  if (!date) return null

  const formattedDate = formatCzechDate(date)

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md"
      >
        <SheetHeader className="mb-6">
          <SheetTitle className="text-lg sm:text-xl capitalize">
            {formattedDate}
          </SheetTitle>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-8rem)] pr-4">
          {dayEntries.length === 0 ? (
            // Empty state
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-16 h-16 rounded-full bg-muted/30 flex items-center justify-center mb-4">
                <FileText className="h-8 w-8 text-muted-foreground/50" />
              </div>
              <h3 className="text-base font-semibold mb-2">
                Žádné záznamy
              </h3>
              <p className="text-sm text-muted-foreground max-w-[250px]">
                Pro tento den zatím nejsou žádné záznamy.
              </p>
            </div>
          ) : (
            // Timeline view
            <div className="space-y-4">
              {dayEntries.map((entry, index) => {
                const zone = getZoneById(entry.zoneId)
                if (!zone) return null

                return (
                  <motion.div
                    key={entry.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <div className="flex gap-3">
                      {/* Timeline dot and line */}
                      <div className="flex flex-col items-center">
                        <div
                          className="w-3 h-3 rounded-full border-2 border-background shadow-sm"
                          style={{ backgroundColor: zone.color }}
                        />
                        {index < dayEntries.length - 1 && (
                          <div className="w-0.5 flex-1 bg-border mt-2" />
                        )}
                      </div>

                      {/* Entry content */}
                      <div className="flex-1 pb-6">
                        {/* Time */}
                        <div className="flex items-center gap-2 mb-2">
                          <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                          <span className="text-xs font-medium text-muted-foreground tabular-nums">
                            {formatTime(entry.timestamp)}
                          </span>
                        </div>

                        {/* Zone card */}
                        <div
                          className={cn(
                            "rounded-lg border p-3 transition-colors",
                            "bg-card hover:bg-accent/5"
                          )}
                          style={{
                            borderColor: zone.color + "40",
                          }}
                        >
                          {/* Zone header */}
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-2xl leading-none">
                              {zone.emoji}
                            </span>
                            <div className="flex-1 min-w-0">
                              <h4 className="text-sm font-semibold truncate">
                                {zone.name}
                              </h4>
                              <p className="text-xs text-muted-foreground">
                                Zóna {zone.number}
                              </p>
                            </div>
                          </div>

                          {/* Notes (if present) */}
                          {entry.note && (
                            <>
                              <Separator className="my-2" />
                              <p className="text-sm text-foreground/80 leading-relaxed">
                                {entry.note}
                              </p>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )
              })}
            </div>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  )
}
